import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    Container,
    Tooltip,
    Card,
    Stack,
    Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ViewListIcon from '@mui/icons-material/ViewList';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const STATE_NAMES = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
    'DC': 'District of Columbia'
};

const MarketMapContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #11224d 0%, #0e1d3a 100%)',
    borderRadius: '8px',
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(3),
    color: 'white',
    boxShadow: '0 2px 8px rgba(17, 34, 77, 0.15)',
}));

const StateRowPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(0.75, 1.5), // Reduced padding
    marginBottom: theme.spacing(0.75), // Tighter spacing between boxes
    transition: 'all 0.2s ease',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#ffffff',
    '&:hover': {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        borderColor: '#ffb300',
    }
}));

const StateName = styled(Typography)(({ theme }) => ({
    fontWeight: '700',
    fontSize: '0.85rem', // Smaller text
    color: '#11224d',
    marginBottom: '2px',
    letterSpacing: '0.2px',
}));

const MetricBox = styled(Box)(({ bgcolor }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '2px 8px', // Slimmer metric boxes
    backgroundColor: bgcolor,
    borderRadius: '4px',
    minWidth: '75px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
}));

const HorizontalBar = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '2px',
});
const getMarketTemperature = (loadsPercent) => {
    if (loadsPercent >= 75) return { label: 'Very Hot', color: '#d32f2f', icon: WhatshotIcon };
    if (loadsPercent >= 62.5) return { label: 'Hot', color: '#f57c00', icon: WhatshotIcon };
    if (loadsPercent >= 37.5) return { label: 'Neutral', color: '#ffb300', icon: ThermostatIcon };
    if (loadsPercent >= 25) return { label: 'Cold', color: '#1976d2', icon: AcUnitIcon };
    return { label: 'Very Cold', color: '#0d47a1', icon: AcUnitIcon };
};

const MarketMap = () => {
    const [marketData, setMarketData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totals, setTotals] = useState({ trucks: 0, loads: 0, states: 0 });

    useEffect(() => {
        const fetchMarketData = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const response = await fetch(`${API_BASE_URL}/api/market/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                
                if (!Array.isArray(data)) {
                    console.error('Unexpected data format:', data);
                    setError('Invalid data format received from server');
                    return;
                }

                const totalTrucks = data.reduce((sum, item) => sum + (item.trucksPosted || 0), 0);
                const totalLoads = data.reduce((sum, item) => sum + (item.loadsPosted || 0), 0);
                
                setTotals({ 
                    trucks: totalTrucks, 
                    loads: totalLoads, 
                    states: data.length 
                });

                const processedData = data.map(item => {
                    const totalUnits = item.loadsPosted + item.trucksPosted;
                    const loadsPercent = totalUnits > 0 ? (item.loadsPosted / totalUnits) * 100 : 50;
                    const ratio = item.trucksPosted > 0 ? item.loadsPosted / item.trucksPosted : item.loadsPosted > 0 ? 999 : 0;
                    
                    return {
                        ...item,
                        fullName: STATE_NAMES[item.state] || item.state,
                        loadsPercent: loadsPercent,
                        ratio: ratio,
                        temperature: getMarketTemperature(loadsPercent)
                    };
                });

                setMarketData(processedData);
            } catch (err) {
                setError('Failed to fetch market data. Please try again later.');
                console.error("Market data fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMarketData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '80vh',
                gap: 2
            }}>
                <CircularProgress size={50} thickness={4} sx={{ color: '#ffb300' }} />
                <Typography variant="body2" color="text.secondary">
                    Loading market data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            </Container>
        );
    }

    return (
        <MarketMapContainer maxWidth="xl">
            <HeaderSection>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography 
                            variant="h5" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 600,
                                mb: 1,
                                letterSpacing: '-0.3px'
                            }}
                        >
                            ShipperDock Market Intelligence Dashboard
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontWeight: 400,
                            }}
                        >
                            Real-time freight capacity across all states
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box sx={{ 
                                        backgroundColor: 'rgba(255, 179, 0, 0.15)', 
                                        borderRadius: '6px',
                                        p: 1,
                                        mb: 0.5,
                                        display: 'inline-flex'
                                    }}>
                                        <ViewListIcon sx={{ color: '#ffb300', fontSize: 24 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1.25rem' }}>
                                        {totals.loads.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        Active Loads
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box sx={{ 
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)', 
                                        borderRadius: '6px',
                                        p: 1,
                                        mb: 0.5,
                                        display: 'inline-flex'
                                    }}>
                                        <LocalShippingIcon sx={{ color: 'white', fontSize: 24 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1.25rem' }}>
                                        {totals.trucks.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        Active Trucks
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box sx={{ 
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)', 
                                        borderRadius: '6px',
                                        p: 1,
                                        mb: 0.5,
                                        display: 'inline-flex'
                                    }}>
                                        <LocationOnIcon sx={{ color: 'white', fontSize: 24 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1.25rem' }}>
                                        {totals.states}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        Active States
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </HeaderSection>

            <Box sx={{ 
                mb: 1.5, 
                p: 1.5, 
                backgroundColor: 'white', 
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
            }}>
                <Typography variant="subtitle2" sx={{ color: '#11224d', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                    Market Temperature Scale
                </Typography>
                
                <Box sx={{ position: 'relative', height: '30px', mb: 1.5, mt: 1 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        height: '16px', 
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1.5px solid #e0e0e0'
                    }}>
                        {/* Very Cold */}
                        <Box sx={{ width: '25%', background: 'linear-gradient(to right, #0d47a1, #1976d2)', position: 'relative' }}>
                            <Typography variant="caption" sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.6rem'
                            }}>
                                Very Cold
                            </Typography>
                        </Box>
                        {/* Cold */}
                        <Box sx={{ width: '12.5%', backgroundColor: '#1976d2', position: 'relative' }}>
                            <Typography variant="caption" sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.55rem'
                            }}>
                                Cold
                            </Typography>
                        </Box>
                        {/* Neutral */}
                        <Box sx={{ width: '25%', background: 'linear-gradient(to right, #1976d2, #ffb300)', position: 'relative' }}>
                            <Typography variant="caption" sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.6rem'
                            }}>
                                Neutral
                            </Typography>
                        </Box>
                        {/* Hot */}
                        <Box sx={{ width: '12.5%', backgroundColor: '#f57c00', position: 'relative' }}>
                            <Typography variant="caption" sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.55rem'
                            }}>
                                Hot
                            </Typography>
                        </Box>
                        {/* Very Hot */}
                        <Box sx={{ width: '25%', background: 'linear-gradient(to right, #f57c00, #d32f2f)', position: 'relative' }}>
                            <Typography variant="caption" sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.6rem'
                            }}>
                                Very Hot
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3, px: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>0%</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>25%</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>50%</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>75%</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>100%</Typography>
                    </Box>
                </Box>

                <Box
  sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1,
    mt: 2,
  }}
>
  {/* LEFT SIDE — Temperature Scale */}
  <Stack
    direction="row"
    spacing={1}
    flexWrap="wrap"
    useFlexGap
    sx={{ flex: 1 }}
  >
    <Chip
      icon={<AcUnitIcon sx={{ fontSize: 16 }} />}
      label="Very Cold (0-25%)"
      size="small"
      sx={{
        backgroundColor: '#e1f5fe',
        color: '#0d47a1',
        fontWeight: 500,
        fontSize: '0.75rem',
        borderRadius: '6px',
      }}
    />
    <Chip
      icon={<AcUnitIcon sx={{ fontSize: 16 }} />}
      label="Cold (25-37.5%)"
      size="small"
      sx={{
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        fontWeight: 500,
        fontSize: '0.75rem',
        borderRadius: '6px',
      }}
    />
    <Chip
      icon={<ThermostatIcon sx={{ fontSize: 16 }} />}
      label="Neutral (37.5-62.5%)"
      size="small"
      sx={{
        backgroundColor: '#fff8e1',
        color: '#ffb300',
        fontWeight: 500,
        fontSize: '0.75rem',
        borderRadius: '6px',
      }}
    />
    <Chip
      icon={<WhatshotIcon sx={{ fontSize: 16 }} />}
      label="Hot (62.5-75%)"
      size="small"
      sx={{
        backgroundColor: '#fff3e0',
        color: '#f57c00',
        fontWeight: 500,
        fontSize: '0.75rem',
        borderRadius: '6px',
      }}
    />
    <Chip
      icon={<WhatshotIcon sx={{ fontSize: 16 }} />}
      label="Very Hot (75-100%)"
      size="small"
      sx={{
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        fontWeight: 500,
        fontSize: '0.75rem',
        borderRadius: '6px',
      }}
    />
  </Stack>

  {/* RIGHT SIDE — Visual Bar Representation */}
  <Box
    sx={{
      pl: 1,
      borderLeft: '1px solid #e0e0e0',
      minWidth: 220,
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        color: '#11224d',
        fontWeight: 600,
        mb: 1,
        fontSize: '0.875rem',
      }}
    >
      Visual Bar Representation
    </Typography>

    <Stack direction="row" spacing={3} alignItems="center">
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '6px',
            backgroundColor: '#ffb300',
            boxShadow: '0 2px 4px rgba(255,179,0,0.3)',
          }}
        />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
          Loads
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '6px',
            backgroundColor: '#11224d',
            boxShadow: '0 2px 4px rgba(17,34,77,0.3)',
          }}
        />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
          Trucks
        </Typography>
      </Stack>
    </Stack>
  </Box>
</Box>
            </Box>

            {marketData.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2, m: 3 }}>
                    No market data available at the moment.
                </Alert>
            ) : (
                marketData.map(item => {
                    const TempIcon = item.temperature.icon;
                    const totalUnits = item.loadsPosted + item.trucksPosted;
                    const loadsPercent = totalUnits > 0 ? (item.loadsPosted / totalUnits) * 100 : 50;
                    const trucksPercent = 100 - loadsPercent;
                    
                    return (
                        <StateRowPaper key={item.state} elevation={0}>
                            <StateName>
                                {item.fullName}
                            </StateName>
                            
                            <HorizontalBar>
                                <MetricBox bgcolor="#fff8e1">
                                    <ViewListIcon sx={{ color: '#ffb300', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#11224d', fontSize: '1rem', lineHeight: 1 }}>
                                            {item.loadsPosted}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                                            Loads
                                        </Typography>
                                    </Box>
                                </MetricBox>

                                <Tooltip 
                                    title={`${loadsPercent.toFixed(1)}% loads • ${trucksPercent.toFixed(1)}% trucks`}
                                    arrow
                                    placement="top"
                                >
                                    <Box sx={{ 
                                        flexGrow: 1, 
                                        height: '6px', 
                                        borderRadius: '3px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        cursor: 'pointer',
                                    }}>
                                        <Box sx={{
                                            width: `${loadsPercent}%`,
                                            height: '100%',
                                            backgroundColor: '#ffb300',
                                            transition: 'all 0.3s ease',
                                        }} />
                                        <Box sx={{
                                            width: `${trucksPercent}%`,
                                            height: '100%',
                                            backgroundColor: '#11224d',
                                            transition: 'all 0.3s ease',
                                        }} />
                                        <Box sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: `${loadsPercent}%`,
                                            transform: 'translate(-50%, -50%)',
                                            width: '3px',
                                            height: '14px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '1.5px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                            zIndex: 1,
                                        }} />
                                    </Box>
                                </Tooltip>

                                <MetricBox bgcolor="#e8ecf1">
                                    <LocalShippingIcon sx={{ color: '#11224d', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#11224d', fontSize: '1rem', lineHeight: 1 }}>
                                            {item.trucksPosted}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                                            Trucks
                                        </Typography>
                                    </Box>
                                </MetricBox>

                                <Tooltip 
                                    title={`Ratio: ${item.ratio.toFixed(2)} loads per truck`}
                                    arrow
                                    placement="top"
                                >
                                    <Chip
                                        icon={<TempIcon sx={{ fontSize: 14 }} />}
                                        label={item.temperature.label}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${item.temperature.color}15`,
                                            color: item.temperature.color,
                                            fontWeight: 600,
                                            fontSize: '0.7rem', 
                                            height: '32px', 
                                            minWidth: '100px',
                                            border: `1.5px solid ${item.temperature.color}`,
                                            borderRadius: '6px',
                                            '& .MuiChip-icon': {
                                                color: item.temperature.color
                                            }
                                        }}
                                    />
                                </Tooltip>
                            </HorizontalBar>
                        </StateRowPaper>
                    );
                })
            )}

            <Box sx={{ mt: 3, textAlign: 'center', pb: 2 }}>
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                    ShipperDock Market Intelligence Dashboard
                </Typography>
            </Box>
        </MarketMapContainer>
    );
};

export default MarketMap;